import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceFiles = ({ reservationId }) => {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reservations/${reservationId}/invoice-files`);
      setItems(res.data?.items || []);
    } catch (e) {
      setItems([]);
    }
  }, [reservationId]);

  useEffect(() => { if (reservationId) load(); }, [reservationId, load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Sadece PDF yükleyebilirsiniz'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('Dosya 25MB’dan büyük olamaz'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${API}/reservations/${reservationId}/invoice-files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Fatura yüklendi');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fatura yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const view = (id) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/invoice-files/${id}/download?auth=${encodeURIComponent(token || '')}`, '_blank');
  };

  const remove = async (id) => {
    if (!window.confirm('Bu faturayı silmek istiyor musunuz?')) return;
    try {
      await axios.delete(`${API}/invoice-files/${id}`);
      toast.success('Fatura silindi');
      await load();
    } catch (e) {
      toast.error('Fatura silinemedi');
    }
  };

  return (
    <Card className="border-purple-200" data-testid="invoice-files-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-purple-900">
          <FileText className="w-4 h-4 text-purple-600" /> Fatura Dosyaları (PDF)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-3" data-testid="invoice-files-empty">
            Henüz fatura yüklenmedi
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-2.5 bg-purple-50 rounded-lg" data-testid={`invoice-file-${f.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-purple-900 truncate">{f.original_filename}</p>
                  <p className="text-xs text-purple-600">
                    {(f.size / 1024).toFixed(0)} KB · {String(f.created_at || '').slice(0, 10)}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-purple-700" onClick={() => view(f.id)} data-testid={`invoice-view-${f.id}`}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => remove(f.id)} data-testid={`invoice-delete-${f.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <label className="block">
          <input type="file" accept="application/pdf" onChange={onUpload} className="hidden" data-testid="invoice-file-input" />
          <Button asChild disabled={uploading} className="w-full bg-purple-600 hover:bg-purple-700 cursor-pointer">
            <span data-testid="invoice-upload-btn">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Yükleniyor...' : 'Fatura Yükle (PDF)'}
            </span>
          </Button>
        </label>
      </CardContent>
    </Card>
  );
};

export default InvoiceFiles;
